import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { ShimmerTable } from 'react-shimmer-effects'
import moment from 'moment-timezone'
import {
  useErrorService,
  useUserService,
  useNotificationService,
} from '@abroad/components'
import { PageHeader } from '../components/common'
import API from '../utils/API'
import CancelSessionModal from '../components/sessions/CancelSessionModal'
import QuestSessionSchedulingCalendar from '../components/sessions/QuestSessionSchedulingCalendar'
import ScheduleGuidelineModal from '../components/sessions/ScheduleGuidelineModal'
import { getSortedTimezones } from '../utils/timezone'
import {
  gtmCancelSessionAction,
  gtmScheduleSessionAction,
  gtmScheduleMoreIndividualSessionAction,
} from '../components/quests/gtmfunctions'

const QuestSessionList = () => {
  const history = useHistory()
  const { questId } = useParams()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { user } = useUserService()

  const [isLoading, setIsLoading] = useState(true)
  const [scheduledSessions, setScheduledSessions] = useState([])
  const [toBeScheduledSessions, setToBeScheduledSessions] = useState([])
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [sessionPendingCancel, setSessionPendingCancel] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedRowForCalendar, setSelectedRowForCalendar] = useState(null)
  const [showScheduleGuideline, setShowScheduleGuideline] = useState(false)
  const [selectedCoachRow, setSelectedCoachRow] = useState(null)

  const userId = user?._id

  // Get user's timezone for date/time display
  const userTimezone = useMemo(() => {
    const timezone = user?.timezone
    let browserTimezone = ''
    try {
      if (
        typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat &&
        Intl.DateTimeFormat().resolvedOptions
      ) {
        browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      }
    } catch (e) {
      browserTimezone = ''
    }

    const detectedTimezone = timezone || browserTimezone

    if (detectedTimezone) {
      const timezones = getSortedTimezones()
      const timezoneExists = timezones.some(
        (tz) => tz.value === detectedTimezone,
      )
      if (timezoneExists) {
        return detectedTimezone
      }
    }

    return browserTimezone || 'Asia/Kolkata'
  }, [user])

  const fetchSessions = useCallback(async () => {
    try {
      if (!userId || !questId) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      const { data } = await API.session.getScheduling(userId, questId)
      if (data?.success) {
        setScheduledSessions(data?.scheduled || [])
        setToBeScheduledSessions(data?.toBeScheduled || [])
      }
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      Error.showError(e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, questId])

  const handleScheduleSuccess = () => {
    // Refresh sessions after successful scheduling
    fetchSessions()
    // Hide calendar and return to table view
    setSelectedRowForCalendar(null)
  }

  useEffect(() => {
    fetchSessions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleScheduleClick = (row) => {
    const isScheduleMore =
      row?.totalSessions &&
      row?.remainingSessions !== undefined &&
      row?.totalSessions !== row?.remainingSessions

    if (isScheduleMore) {
      gtmScheduleMoreIndividualSessionAction(
        getDescription(row),
        user?._id,
        questId,
      )
    } else {
      gtmScheduleSessionAction(getDescription(row), user?._id, questId)
    }

    // Check if conditional scheduling is enabled
    const isConditionalSchedulingEnabled =
      row?.isConditionalSchedulingEnabled || false

    if (isConditionalSchedulingEnabled) {
      // Show calendar for the selected row (conditional flow)
      setSelectedRowForCalendar(row)
    } else {
      // Show guideline modal (old flow)
      setSelectedCoachRow(row)
      setShowScheduleGuideline(true)
    }
  }

  const handleCalendarBack = () => {
    // Hide calendar and return to table view
    setSelectedRowForCalendar(null)
  }

  const closeScheduleModal = () => {
    setSelectedCoachRow(null)
    setShowScheduleGuideline(false)
  }

  const isFirstSessionScheduled = (coachRow) => {
    if (coachRow?.remainingSessions !== coachRow?.totalSessions) {
      return true
    }
    return false
  }

  const handleReschedule = (scheduledRow) => {
    history.push(`/your-experiences/${questId}/reschedule-session`, {
      scheduledSession: scheduledRow,
    })
  }

  const openCancelModal = (sessionRow) => {
    setSessionPendingCancel(sessionRow)
    setShowCancelModal(true)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setSessionPendingCancel(null)
  }

  const handleCancelConfirm = async () => {
    if (!sessionPendingCancel) {
      return
    }

    gtmCancelSessionAction(
      getDescription(sessionPendingCancel),
      getTimeValue(sessionPendingCancel),
      user?._id,
      questId,
    )

    const coachId = sessionPendingCancel?.coachId
    const eventId = sessionPendingCancel?.eventId || sessionPendingCancel?._id

    if (!coachId || !eventId) {
      Notification.showNotification(
        'Unable to find session details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsCancelling(true)
      const { data } = await API.session.cancelSession(coachId, eventId)

      if (!data?.success) {
        Notification.showNotification(
          data?.message ||
            'Unable to cancel the session. Please try again later.',
          'danger',
        )
        return
      }

      Notification.showNotification(
        data?.message || 'Session cancelled successfully.',
        'success',
      )
      closeCancelModal()
      await fetchSessions()
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCancelling(false)
    }
  }

  const getTimeValue = (row) => {
    if (row?.timeLabel) {
      return row.timeLabel
    }
    if (row?.slotStart) {
      const dateTime = moment.utc(row.slotStart).tz(userTimezone)
      const date = dateTime.format('MM/DD/YYYY')
      const time = dateTime.format('LT')
      return `${date} ${time}`
    }
    if (row?.scheduledAt) {
      const dateTime = moment.utc(row.scheduledAt).tz(userTimezone)
      const date = dateTime.format('MM/DD/YYYY')
      const time = dateTime.format('LT')
      return `${date} ${time}`
    }
    return row?.time || '-'
  }

  const getDurationLabel = (row) => {
    if (row?.durationLabel) return row.durationLabel
    if (row?.duration) {
      return `${row.duration} Minutes`
    }
    return '-'
  }

  const getFname = (person) => {
    const name = person?.fname
    return name && String(name).trim() ? String(name).trim() : '-'
  }
  const getFullName = (person) => {
    const name = person?.fullName
    return name && String(name).trim()
      ? String(name).trim()
      : person?.fullName || '-'
  }

  const getDescription = (row) => {
    const sowType = row?.sowType || row?.sow?.sowType || '-'
    const clientName = getFname(row?.client)
    const coachName = getFname(row?.coach)

    if (sowType === 'QUEST') {
      const formatQuestSowDescription = (sessionLabelPart) => {
        const label = (sessionLabelPart || '').trim()
        return label
          ? `${label}: ${coachName} & ${clientName}`
          : `Wisdom Sessions: ${coachName} & ${clientName}`
      }

      const remainingSessions = row?.remainingSessions
      const totalSessions = row?.totalSessions

      // If remainingSessions === totalSessions, show simple format
      if (
        remainingSessions !== undefined &&
        totalSessions !== undefined &&
        remainingSessions === totalSessions
      ) {
        return formatQuestSowDescription('')
      }

      // For scheduled sessions, use single sessionIndex from schedulingCondition
      if (row?.schedulingCondition?.sessionIndex) {
        const sessionIndex = row.schedulingCondition.sessionIndex
        let sessionLabel = ''
        if (sessionIndex === 1) {
          sessionLabel = 'Preparation Session'
        } else if (sessionIndex === 2) {
          sessionLabel = ' Integration Session'
        } else if (sessionIndex === 3) {
          sessionLabel = 'Experience Session'
        } else if (sessionIndex) {
          sessionLabel = `Session ${sessionIndex}`
        }

        return formatQuestSowDescription(sessionLabel)
      }

      // For toBeScheduled sessions, check if there are multiple schedulingConditions
      if (
        Array.isArray(row?.schedulingConditions) &&
        row.schedulingConditions.length > 0
      ) {
        // Get all sessionIndexes and convert to labels
        const sessionLabels = row.schedulingConditions
          .map((condition) => {
            const sessionIndex = condition?.sessionIndex
            if (sessionIndex === 1) {
              return 'Preparation Session'
            } else if (sessionIndex === 2) {
              return ' Integration Session'
            } else if (sessionIndex === 3) {
              return 'Experience Session'
            } else if (sessionIndex) {
              return `Session ${sessionIndex}`
            }
            return null
          })
          .filter((label) => label !== null)
          .sort((a, b) => {
            // Sort: Preparation Session first, then  Integration Session, then Session numbers
            if (a === 'Preparation Session') return -1
            if (b === 'Preparation Session') return 1
            if (a === ' Integration Session') return -1
            if (b === ' Integration Session') return 1
            if (a === 'Experience Session')
              return b === 'Experience Session' ? 0 : -1
            if (b === 'Experience Session') return 1
            // Extract number from "Session X" for sorting
            const numA = parseInt(a.replace('Session ', ''), 10) || 0
            const numB = parseInt(b.replace('Session ', ''), 10) || 0
            return numA - numB
          })

        if (sessionLabels.length > 0) {
          const combinedLabel = sessionLabels.join(' and ')
          return formatQuestSowDescription(combinedLabel)
        }
      }

      // Fallback: try direct sessionIndex
      const sessionIndex = row?.sessionIndex
      let sessionLabel = ''
      if (sessionIndex === 1) {
        sessionLabel = 'Preparation Session'
      } else if (sessionIndex === 2) {
        sessionLabel = ' Integration Session'
      } else if (sessionIndex === 3) {
        sessionLabel = 'Experience Session'
      } else if (sessionIndex) {
        sessionLabel = `Session ${sessionIndex}`
      } else {
        sessionLabel = 'Preparation and Integration Session'
      }

      return formatQuestSowDescription(sessionLabel)
    }

    // const isGrowth =row?.isGrowthEvent
    // if (isGrowth) {
    //   return `${clientName} <> ${coachName} <> Growth Session`
    // }
    // const isScheduled = !!(row?.slotStart || row?.scheduledAt)
    // if (isScheduled) {
    //   return `${clientName} <> ${coachName} <> Coaching Session`
    // }
    // return `${clientName} <> ${coachName} <> Coaching Plan`
  }

  // Check if session is within 24 hours
  const isWithin24Hours = (row) => {
    if (!row?.slotStart) {
      return false
    }

    const sessionStart = moment.utc(row.slotStart)
    const now = moment.utc()
    const hoursUntilSession = sessionStart.diff(now, 'hours', true)

    return hoursUntilSession > 0 && hoursUntilSession <= 24
  }

  // Render actions for scheduled sessions
  const renderScheduledSessionActions = (row, index) => {
    const within24Hours = isWithin24Hours(row)
    const isRescheduleDisabled =
      row?.isScheduleEnabled === false || within24Hours

    const renderRescheduleButton = () => {
      if (isRescheduleDisabled) {
        return (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip
                style={{
                  '--bs-tooltip-max-width': '22rem',
                  maxWidth: '22rem',
                }}
                id={`tooltip-reschedule-${index}`}
                className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                  }}>
                  {within24Hours
                    ? 'You cannot reschedule or cancel this session within 24 hours of its start time.'
                    : 'Your coach will reach out to you.'}
                </div>
              </Tooltip>
            }>
            <span className='d-inline-block'>
              <div
                className='text-muted pe-2'
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}>
                Reschedule
              </div>
            </span>
          </OverlayTrigger>
        )
      }

      return (
        <div
          className='pe-2 text-saffron-700 hover cursor-pointer'
          onClick={() => handleReschedule(row)}
          style={{ cursor: 'pointer' }}>
          Reschedule
        </div>
      )
    }

    const renderCancelButton = () => {
      if (within24Hours) {
        return (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip
                style={{
                  '--bs-tooltip-max-width': '22rem',
                  maxWidth: '22rem',
                }}
                id={`tooltip-cancel-${index}`}
                className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                  }}>
                  You cannot reschedule or cancel this session within 24 hours
                  of its start time.
                </div>
              </Tooltip>
            }>
            <span className='d-inline-block'>
              <div
                className='text-danger'
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}>
                Cancel
              </div>
            </span>
          </OverlayTrigger>
        )
      }

      return (
        <div
          className='text-danger hover cursor-pointer'
          onClick={() => openCancelModal(row)}
          style={{ cursor: 'pointer' }}>
          Cancel
        </div>
      )
    }

    return (
      <span className='d-flex justify-content-center align-items-center'>
        {renderRescheduleButton()}
        {renderCancelButton()}
      </span>
    )
  }

  const renderEmptyRow = (columns, text) => (
    <tr>
      <td className='text-center s8 py-3' colSpan={columns}>
        {text}
      </td>
    </tr>
  )

  const renderHelmet = () => (
    <Helmet>
      <title>{'Quest Sessions | Abroad'}</title>
      <meta name='title' content={'Quest Sessions | Abroad'}></meta>
      <meta property='og:title' content={'Quest Sessions | Abroad'}></meta>
      <meta property='twitter:title' content={'Quest Sessions | Abroad'}></meta>
      <link
        rel='canonical'
        href={`${process.env.REACT_APP_DOMAIN}/your-experiences/${questId}/session-list`}></link>
      <meta
        property='og:url'
        content={`${process.env.REACT_APP_DOMAIN}/your-experiences/${questId}/session-list`}></meta>
      <meta
        property='twitter:url'
        content={`${process.env.REACT_APP_DOMAIN}/your-experiences/${questId}/session-list`}></meta>
    </Helmet>
  )

  // Determine if we should show calendar (when scheduled is empty and toBeScheduled has items, and no row is selected, and conditional scheduling is enabled, and isScheduleEnabled is true)
  const shouldShowCalendar = useMemo(() => {
    if (
      isLoading ||
      scheduledSessions.length !== 0 ||
      toBeScheduledSessions.length === 0 ||
      selectedRowForCalendar
    ) {
      return false
    }

    // Check if conditional scheduling is enabled and isScheduleEnabled is true for the first toBeScheduled session
    const firstToBeScheduled = toBeScheduledSessions[0]
    const isConditionalSchedulingEnabled =
      firstToBeScheduled?.isConditionalSchedulingEnabled || false
    const isScheduleEnabled = firstToBeScheduled?.isScheduleEnabled !== false

    return isConditionalSchedulingEnabled && isScheduleEnabled
  }, [
    isLoading,
    scheduledSessions.length,
    toBeScheduledSessions,
    selectedRowForCalendar,
  ])

  // Get coach details for calendar (from toBeScheduled or scheduled)
  const coachDetailsForCalendar = useMemo(() => {
    let coachData = null
    if (toBeScheduledSessions.length > 0) {
      coachData = toBeScheduledSessions[0]
    } else if (scheduledSessions.length > 0) {
      coachData = scheduledSessions[0]
    }

    // Add flag to indicate this is from quest route
    if (coachData) {
      return {
        ...coachData,
        isFromQuest: true,
      }
    }
    return null
  }, [toBeScheduledSessions, scheduledSessions])

  // Filter coachDetails to only include remaining sessions from schedulingConditions
  const filteredCoachDetails = useMemo(() => {
    if (!selectedRowForCalendar) return null

    // Get only the schedulingConditions that are in the toBeScheduled row
    const remainingSchedulingConditions =
      selectedRowForCalendar.schedulingConditions || []

    // Create a filtered coachDetails object with only remaining sessions
    return {
      ...selectedRowForCalendar,
      schedulingConditions: remainingSchedulingConditions,
      // Update totalSessions to reflect only remaining sessions
      totalSessions: remainingSchedulingConditions.length,
      remainingSessions: remainingSchedulingConditions.length,
      // Add flag to indicate this is from quest route
      isFromQuest: true,
    }
  }, [selectedRowForCalendar])

  // Show loading shimmer when fetching data
  if (isLoading) {
    return (
      <>
        {renderHelmet()}
        <PageHeader
          title='Sessions'
          version='small'
          backArrowAction={() => history.goBack()}
          showBackArrow={true}
        />
        <div className='mt-32px'>
          <ShimmerTable col={6} row={4} />
        </div>
      </>
    )
  }

  // Show calendar if scheduled is empty (first time scheduling)
  if (shouldShowCalendar) {
    return (
      <>
        {renderHelmet()}
        <PageHeader
          title='Sessions'
          version='small'
          backArrowAction={() => history.goBack()}
          showBackArrow={true}
        />
        <QuestSessionSchedulingCalendar
          questId={questId}
          onScheduleSuccess={handleScheduleSuccess}
          coachDetails={coachDetailsForCalendar}
        />
      </>
    )
  }

  // Show calendar for a specific row when clicked (for remaining sessions)
  if (selectedRowForCalendar) {
    return (
      <>
        {renderHelmet()}
        <PageHeader
          title='Sessions'
          version='small'
          backArrowAction={handleCalendarBack}
          showBackArrow={true}
        />
        <QuestSessionSchedulingCalendar
          questId={questId}
          onScheduleSuccess={handleScheduleSuccess}
          coachDetails={filteredCoachDetails}
        />
      </>
    )
  }

  return (
    <>
      {renderHelmet()}
      <PageHeader
        title='Sessions'
        version='small'
        backArrowAction={() => history.goBack()}
        showBackArrow={true}
      />
      <div className='mt-32px'>
        <section className='mb-4'>
          <h3 className='s3 mb-3'>To Be Scheduled</h3>
          <div className='table-responsive'>
            {isLoading ? (
              <ShimmerTable col={4} row={4} />
            ) : (
              <Table className='custom-table align-middle'>
                <thead>
                  <tr className='s10a border-dark'>
                    {/* <th width='5%'>#</th> */}
                    <th width='75%' className='text-start'>
                      DESCRIPTION
                    </th>
                    <th width='25%' className='text-center'>
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {toBeScheduledSessions.length === 0 &&
                    renderEmptyRow(3, 'No sessions to schedule.')}
                  {toBeScheduledSessions.map((row, index) => {
                    return (
                      <tr key={`${row?.coachId || row?.coachName}-${index}`}>
                        {/* <td width='5%'>{index + 1}</td> */}
                        <td width='75%' className='text-start'>
                          {getDescription(row)}
                        </td>
                        <td width='25%' className='text-center'>
                          {row?.isScheduleEnabled === false ? (
                            <OverlayTrigger
                              placement='top'
                              overlay={
                                <Tooltip
                                  style={{
                                    '--bs-tooltip-max-width': '22rem',
                                    maxWidth: '22rem',
                                  }}
                                  id={`tooltip-schedule-${index}`}
                                  className='font-open-sans font-16 font-semibold leading-7 text-center schedule-info'>
                                  <div
                                    style={{
                                      width: '100%',
                                      maxWidth: '100%',
                                      whiteSpace: 'nowrap',
                                    }}>
                                    Your coach will reach out to you.
                                  </div>
                                </Tooltip>
                              }>
                              <div
                                className='text-muted'
                                style={{
                                  cursor: 'not-allowed',
                                  opacity: 0.6,
                                }}>
                                Schedule
                              </div>
                            </OverlayTrigger>
                          ) : (
                            <div
                              className='text-saffron-700 hover cursor-pointer'
                              onClick={() => handleScheduleClick(row)}
                              style={{ cursor: 'pointer' }}>
                              {row?.totalSessions &&
                              row?.remainingSessions !== undefined &&
                              row?.totalSessions !== row?.remainingSessions
                                ? 'Schedule More'
                                : 'Schedule'}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </section>

        <section>
          <h3 className='s3 mb-3'>Scheduled</h3>
          <div className='table-responsive'>
            {isLoading ? (
              <ShimmerTable col={6} row={4} />
            ) : (
              <Table className='custom-table align-middle'>
                <thead>
                  <tr className='s10a border-dark'>
                    {/* <th width='7%'>#</th> */}
                    <th width='35%' className='text-start'>
                      DESCRIPTION
                    </th>
                    <th width='25%' className='text-center'>
                      DATE & TIME
                    </th>
                    <th width='15%' className='text-center'>
                      DURATION
                    </th>
                    <th width='25%' className='text-center'>
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scheduledSessions.length === 0 &&
                    renderEmptyRow(5, 'No scheduled sessions.')}
                  {scheduledSessions.map((row, index) => {
                    return (
                      <tr
                        key={`${
                          row?.eventId || row?._id || row?.sessionId
                        }-${index}`}>
                        {/* <td width='7%'>{index + 1}</td> */}
                        <td width='35%' className='text-start align-middle'>
                          {getDescription(row)}
                        </td>
                        <td width='25%' className='text-center align-middle'>
                          {getTimeValue(row)}
                        </td>
                        <td width='15%' className='text-center align-middle'>
                          {getDurationLabel(row)}
                        </td>
                        <td width='25%' className='text-center align-middle'>
                          {renderScheduledSessionActions(row, index)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </div>
        </section>
      </div>
      <CancelSessionModal
        show={showCancelModal}
        onClose={closeCancelModal}
        onConfirm={handleCancelConfirm}
        coachName={getFullName(sessionPendingCancel?.coachId) || '-'}
        sessionTime={
          sessionPendingCancel ? getTimeValue(sessionPendingCancel) : ''
        }
        isSubmitting={isCancelling}
      />
      <ScheduleGuidelineModal
        show={showScheduleGuideline}
        handleClose={closeScheduleModal}
        coachDetails={selectedCoachRow}
        isFirstSessionScheduled={isFirstSessionScheduled(selectedCoachRow)}
      />
    </>
  )
}

export default QuestSessionList
